import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useI18n, LANGS } from "@/i18n";
import { api } from "@/lib/api";
import { absAsset } from "@/lib/seo";
import { Field, Pick } from "@/app/tool/Options";

const assetPath = (id) => (id ? `/api/public/asset/${id}` : "");

function AssetPicker({ label, hint, kind, value, onChange, testid }) {
  const { t, errMsg } = useI18n();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/seo/asset?kind=${kind}`, fd);
      onChange(data.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <Field label={label}>
      <div className="flex items-center gap-3">
        <div className="h-14 w-24 rounded-lg border border-[var(--line)] bg-white/90 grid place-items-center overflow-hidden" data-testid={`${testid}-preview`}>
          {value ? <img src={absAsset(assetPath(value))} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-[10px] text-neutral-500">—</span>}
        </div>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/x-icon,image/vnd.microsoft.icon" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-input`} />
        <button type="button" onClick={() => ref.current.click()} disabled={busy} className="rounded-full border border-[var(--line)] px-3 py-1.5 text-xs flex items-center gap-1.5 hover:border-emerald-600" data-testid={`${testid}-upload-btn`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}{t("admin.seo.upload")}
        </button>
        {value && <button type="button" onClick={() => onChange("")} className="text-xs text-neutral-400 hover:text-red-400 flex items-center gap-1" data-testid={`${testid}-remove-btn`}><X size={13} />{t("admin.seo.remove")}</button>}
      </div>
      {hint && <p className="text-[11px] text-neutral-500 mt-1">{hint}</p>}
    </Field>
  );
}

const EMPTY_PAGE = { title: "", description: "", keywords: "", og_file_id: "" };

export default function SeoTab() {
  const { t, errMsg } = useI18n();
  const [s, setS] = useState(null);
  const [pages, setPages] = useState([]);
  const [lang, setLang] = useState("tr");
  const [page, setPage] = useState("home");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/seo").then((r) => { setS(r.data.settings); setPages(r.data.pages); }).catch((e) => toast.error(errMsg(e)));
  }, [errMsg]);
  if (!s) return <p className="text-sm text-neutral-400">{t("common.loading")}</p>;

  const setLangText = (k, v) => setS({ ...s, [k]: { ...s[k], [lang]: v } });
  const pm = { ...EMPTY_PAGE, ...(s.pages?.[page]?.[lang] || {}) };
  const setPm = (k, v) => setS({ ...s, pages: { ...s.pages, [page]: { ...(s.pages?.[page] || {}), [lang]: { ...pm, [k]: v } } } });
  const save = async () => {
    setBusy(true);
    try {
      const { updated_at: _u, ...body } = s;
      const { data } = await api.put("/admin/seo", body);
      setS(data.settings);
      window.dispatchEvent(new Event("seo-updated"));
      toast.success(t("settings.saved"));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const langPick = (
    <div className="inline-flex rounded-full border border-[var(--line)] p-0.5 text-xs font-semibold" data-testid="seo-lang-pick">
      {LANGS.map((l) => (
        <button key={l} type="button" onClick={() => setLang(l)} aria-pressed={lang === l} data-testid={`seo-lang-${l}`}
          className={`px-3 py-1 rounded-full ${lang === l ? "bg-emerald-600 text-white" : "text-neutral-400 hover:text-white"}`}>{l.toUpperCase()}</button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-seo">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-neutral-400 mr-auto">{t("admin.seo.intro")}</p>
        <span className="text-xs text-neutral-400">{t("admin.seo.textLang")}</span>{langPick}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <section className="panel p-6 space-y-4">
          <h3 className="font-semibold">{t("admin.seo.site")}</h3>
          <Field label={`${t("admin.seo.siteTitle")} (${lang.toUpperCase()})`}><Input maxLength={200} value={s.title[lang]} onChange={(e) => setLangText("title", e.target.value)} data-testid="seo-site-title" /></Field>
          <Field label={`${t("admin.seo.siteDesc")} (${lang.toUpperCase()})`}><Textarea maxLength={400} rows={3} value={s.description[lang]} onChange={(e) => setLangText("description", e.target.value)} data-testid="seo-site-description" /></Field>
          <Field label={`${t("admin.seo.keywords")} (${lang.toUpperCase()})`}><Input maxLength={300} value={s.keywords[lang]} onChange={(e) => setLangText("keywords", e.target.value)} placeholder={t("admin.seo.keywordsPh")} data-testid="seo-site-keywords" /></Field>
          <Field label={t("admin.seo.siteUrl")}><Input value={s.site_url} onChange={(e) => setS({ ...s, site_url: e.target.value })} placeholder="https://woodesing.com" data-testid="seo-site-url" /></Field>
          <p className="text-[11px] text-neutral-500 -mt-2">{t("admin.seo.siteUrlHint")}</p>
          <AssetPicker label={t("admin.seo.favicon")} hint={t("admin.seo.faviconHint")} kind="favicon" value={s.favicon_file_id} onChange={(v) => setS({ ...s, favicon_file_id: v })} testid="seo-favicon" />
          <AssetPicker label={t("admin.seo.ogImage")} hint={t("admin.seo.ogHint")} kind="og" value={s.og_file_id} onChange={(v) => setS({ ...s, og_file_id: v })} testid="seo-og" />
          <AssetPicker label={t("admin.seo.paymentLogos")} hint={t("admin.seo.paymentHint")} kind="payment" value={s.payment_logos_file_id} onChange={(v) => setS({ ...s, payment_logos_file_id: v })} testid="seo-payment" />
        </section>
        <section className="panel p-6 space-y-4">
          <h3 className="font-semibold">{t("admin.seo.tracking")}</h3>
          <Field label={t("admin.seo.gaId")}><Input value={s.ga_id} onChange={(e) => setS({ ...s, ga_id: e.target.value })} placeholder="G-XXXXXXXXXX" data-testid="seo-ga-id" /></Field>
          <p className="text-[11px] text-neutral-500 -mt-2">{t("admin.seo.gaHint")}</p>
          <Field label={`${t("admin.seo.gsc")} (${t("common.optional")})`}><Input value={s.gsc_code} onChange={(e) => setS({ ...s, gsc_code: e.target.value })} placeholder='<meta name="google-site-verification" content="…" />' data-testid="seo-gsc" /></Field>
          <Field label={`${t("admin.seo.headScripts")} (${t("common.optional")})`}><Textarea rows={6} maxLength={20000} className="font-mono text-xs" value={s.head_scripts} onChange={(e) => setS({ ...s, head_scripts: e.target.value })} data-testid="seo-head-scripts" /></Field>
          <p className="text-[11px] text-amber-300/90 -mt-2">{t("admin.seo.scriptsWarn")}</p>
          <p className="text-xs text-neutral-400">{t("admin.seo.sitemapInfo")} <a className="text-emerald-400 underline break-all" href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`} target="_blank" rel="noreferrer" data-testid="seo-sitemap-link">/api/sitemap.xml</a></p>
        </section>
      </div>
      <section className="panel p-6 space-y-4" data-testid="seo-pages">
        <div className="flex flex-wrap items-end gap-4">
          <h3 className="font-semibold mr-auto">{t("admin.seo.pageSeo")}</h3>
          <div className="w-56"><Field label={t("admin.seo.page")}><Pick value={page} onChange={setPage} options={pages.map((k) => [k, t(`admin.seo.pages.${k}`)])} testid="seo-page-pick" /></Field></div>
        </div>
        <p className="text-[11px] text-neutral-500">{t("admin.seo.pageHint")}</p>
        <div className="grid lg:grid-cols-2 gap-4">
          <Field label={`${t("admin.seo.metaTitle")} (${lang.toUpperCase()})`}><Input maxLength={200} value={pm.title} onChange={(e) => setPm("title", e.target.value)} data-testid="seo-page-title" /></Field>
          <Field label={`${t("admin.seo.keywords")} (${lang.toUpperCase()})`}><Input maxLength={300} value={pm.keywords} onChange={(e) => setPm("keywords", e.target.value)} data-testid="seo-page-keywords" /></Field>
          <Field label={`${t("admin.seo.metaDesc")} (${lang.toUpperCase()})`}><Textarea maxLength={400} rows={3} value={pm.description} onChange={(e) => setPm("description", e.target.value)} data-testid="seo-page-description" /></Field>
          <AssetPicker label={t("admin.seo.ogImage")} kind="og" value={pm.og_file_id} onChange={(v) => setPm("og_file_id", v)} testid="seo-page-og" />
        </div>
        <p className="text-[11px] text-neutral-500">{t("admin.seo.lengthHint", { t: pm.title.length, d: pm.description.length })}</p>
      </section>
      <button onClick={save} disabled={busy} className="btn-brand rounded-full px-6 py-2.5 font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="seo-save-btn">
        {busy && <Loader2 size={16} className="animate-spin" />}{t("common.save")}
      </button>
    </div>
  );
}
